import ShowPageWrapper from "../show_page_wrapper";
import React, { useEffect, useState } from "react";
import { getShowUUID } from "../../../lib/getters_setters";
import { useDispatch, useSelector } from "react-redux";
import { getShow } from "../../../../action_managers/shows";
import get from "lodash/get";

import { If } from "react-extras";
import { Col, Row } from "react-bootstrap";
import "../shows.scss";
import ShowCampaignAdSettingsModal from "../ad_settings_modal/show_campaign_ad_settings_modal";
import { getShowCampaigns } from "../../../../actions/campaigns";
import HighlightBox from "../../../lib/highlight-box";
import { Link, withRouter } from "react-router-dom";
import { emptyShows } from "../../../../constants/bucketed_audience_size";
import groupBy from "lodash/groupBy";
import { ADVERTISING_OPT_IN_THRESHOLD } from "../../../../constants/misc";
import { useComponentDidMount } from "../../../../lib/hook";
import { setUserAttribute } from "../../../../action_managers/account";
import { HAS_SEEN_ADVERTISING_PAGE } from "../../../../constants/user_attributes";
import { formatMoney } from "../../../../lib/format-money";
import {
  AcceptedCampaignsTable,
  AwaitingAudioUploadTable,
  SentCampaignsTable,
} from "./show_advertising_page_tables";
import { GettingStarted } from "./show_advertising_page_getting_started";

const ShowAdvertisingPage = props => {
  const { history } = props;
  const showUUID = getShowUUID(props);
  const dispatch = useDispatch();
  const showByUUID = useSelector(state => state.shows.shows);
  const isLoading = useSelector(state => state.shows.isLoading);
  const user = useSelector(state => state.user.user);
  const show = get(showByUUID, showUUID, {});
  const hasSetupAdvertising = get(show, "advertisementSettings", false);
  const showTooSmall = get(show, "estimatedWeeklyDownloads", "") < ADVERTISING_OPT_IN_THRESHOLD;
  const hasReceivedAdMarketplaceEmail = !!get(
    user.userAttributes,
    `${showUUID}__invitedToConfigureAds`
  );
  const emptyShowAudienceSizes = emptyShows.map(item => item.value);
  const showNotCalculated = emptyShowAudienceSizes.some(
    size => size === get(show, "bucketedAudienceSize", "")
  );
  const [showModal, setShowModal] = useState(false);

  const campaignItemUUIDs =
    useSelector(state => state.campaignItems.showCampaignItemUUIDs[showUUID]) || [];
  const campaignItemByUUID = useSelector(state => state.campaignItems.campaignItemByUUID);
  const campaignItems = campaignItemUUIDs.map(uuid => campaignItemByUUID[uuid]) || [];
  const {
    sent = [],
    running = [],
    accepted = [],
    completed = [],
    "awaiting-audio-upload": awaitingAudioUpload = [],
  } = groupBy(campaignItems, "state");
  const acceptedPlusItems = accepted
    .concat(awaitingAudioUpload)
    .concat(running)
    .concat(completed);

  useComponentDidMount(() => {
    if (!showTooSmall && !showNotCalculated) {
      const showsWhoHasShownAdvertisingTo = (
        user?.userAttributes?.[HAS_SEEN_ADVERTISING_PAGE] ?? ""
      ).split(",");

      if (!showsWhoHasShownAdvertisingTo.includes(show.uuid)) {
        dispatch(
          setUserAttribute(
            HAS_SEEN_ADVERTISING_PAGE,
            showsWhoHasShownAdvertisingTo.concat(show.uuid).join(",")
          )
        );
      }
    }
  }, [showTooSmall, showNotCalculated]);

  // Sum Cents for all campaigns. Currently only shows amount in USD.
  // We make sure to subtract the RedCircle cut that we took.
  const totalCashMade = acceptedPlusItems.reduce((sum, campaignItem) => {
    const totalCents = get(campaignItem, "paidUpToAmount", 0.0);
    const paidOutCents =
      totalCents - Math.floor((totalCents * campaignItem.advertisingCutBasisPoints) / 10000);
    return sum + paidOutCents;
  }, 0);

  useEffect(() => {
    dispatch(getShow(showUUID));
    dispatch(getShowCampaigns(showUUID));
  }, [dispatch, showUUID]);

  const nonPendingItems = [...accepted, ...running, ...completed];
  const getContent = () => {
    if (isLoading) {
      return null;
    }
    if (hasSetupAdvertising) {
      return (
        <>
          <Row className="margin-bottom--large">
            <Col xs={12} md={6}>
              <HighlightBox title="Campaign Earnings" value={`${formatMoney(totalCashMade)}`} />
            </Col>
            <Col xs={12} md={6}>
              <HighlightBox
                title="Total Campaigns"
                value={acceptedPlusItems.length + sent.length}
              />
            </Col>
          </Row>
          {!campaignItems.length && (
            <div>
              Thanks for setting up your Advertising profile. When you receive campaign offers, they
              will show up here.
            </div>
          )}
          <AwaitingAudioUploadTable
            campaignItems={awaitingAudioUpload}
            history={history}
            showUUID={showUUID}
          />
          <SentCampaignsTable campaignItems={sent} history={history} showUUID={showUUID} />
          <AcceptedCampaignsTable
            campaignItems={nonPendingItems}
            history={history}
            showUUID={showUUID}
          />
        </>
      );
    }

    if (showNotCalculated && !hasReceivedAdMarketplaceEmail) {
      return (
        <div>
          <p>
            Your show is too new to opt-in to advertising. It takes RedCircle a few days to ensure
            that your show is large enough to participate.
          </p>
          <p>Come back in a few days once your show size has been calculated.</p>
        </div>
      );
    }
    if (showTooSmall && !hasReceivedAdMarketplaceEmail) {
      return (
        <div>
          <p>
            Your show isn't large enough to join RedCircle's advertising program. Once your audience
            grows to an average of at least 500 downloads a week, you can opt-in.
          </p>
          <p>
            Check out our <Link to={"/promotions"}>cross-promotions marketplace</Link> to help
            expand your reach.
          </p>
        </div>
      );
    }

    return <GettingStarted showUUID={showUUID} openModal={() => setShowModal(true)} />;
  };

  return (
    <>
      <ShowPageWrapper
        show={show}
        title="Advertising"
        rightLink={() => (
          <If condition={!!hasSetupAdvertising}>
            <div className="btn-link bold pointer" onClick={() => setShowModal(true)}>
              Edit Profile
            </div>
          </If>
        )}>
        {getContent()}
      </ShowPageWrapper>
      <If condition={showModal}>
        <ShowCampaignAdSettingsModal
          show={show}
          close={() => {
            setShowModal(false);
          }}
        />
      </If>
    </>
  );
};

export default withRouter(ShowAdvertisingPage);
